import { computed, inject, provide, reactive, type InjectionKey } from 'vue'
import templateConfigsJson from '@/utils/format-edit/eprintTemplateConfigs.json'
import {
  isCashlessSupported,
  type EprintTemplateConfig
} from '@/utils/format-edit/eprintOptions'

export type EprintPaymentData = {
  paymentType: string
  paymentCost?: number
  printType?: string
  formatId?: string
  paymentHistoryId?: string
  stripePaymentIntentId?: string
  stripeChargeId?: string
  transactionId?: string
  timestamp: string
}

type PaymentSelectorApi = {
  resetDisabled: () => void
  lockAll: () => void
  unlockConvenience: () => void
}

export type EprintActions = {
  openDialog: (
    id: string,
    html: { $el?: { innerHTML: string }; innerHTML?: string },
    selFontStyle4DB?: string,
    resumeAddress?: string,
    isMypage?: boolean
  ) => Promise<void>
  closeDialog: () => void
  onDialogOpened: () => void
  scrollToTop: () => void
  changePaymentMethod: () => void
  eprintPDF2: () => void
  eprintAtokakin: (paymentData?: unknown) => void
  handleSevenElevenClick: () => void
  handleCreditCardPaymentStart: () => void
  handlePaymentSuccess: (paymentData: unknown) => void
  handlePaymentError: (error: unknown) => void
  handlePaypayPayment: () => void
  handleEprintFailureRefund: (errorMessage: string) => Promise<void>
  savePaymentLog: (
    paymentType: string,
    paymentCost: number,
    paymentStatus: string,
    transactionId?: string
  ) => Promise<unknown>
  updatePaymentLogStatus: (status: string, transactionId?: string) => void
  handleGetPaypayCodeEprint: () => void
  savePaypayEprintTempData: (
    merchantPaymentId: string,
    url: string,
    expiryDate?: number
  ) => void
  openPayPayEprintSite: () => void
  completePaypayEprint: (merchantPaymentId: string) => void
  stopPaypayRecheck: () => void
  searchNearCurrentLocation: () => void
  searchNearResumeAddress: () => void
  recenterToCurrentLocation: () => void
  openMapAppNearCurrentLocation: () => void
  openMapAppNearResumeAddress: () => void
  bindMapEl: (el: HTMLElement | null) => void
  onQRCodeLoad: () => void
  onQRCodeError: () => void
  retryQRCode: () => void
  handlePrintTypeChange: (clickedId: string) => void
  handleOtherSizesToggle: (activeNames: string[]) => void
  handleTermsLinkClick: () => void
  dataChanged: () => void
}

const EPRINT_ACTIONS_KEY: InjectionKey<EprintActions> = Symbol('eprintActions')

export type EprintSession = {
  visible: boolean
  eprintPdfEnabled: boolean
  resultname: string
  isNotLetter: boolean
  eprintResultCode: string
  eprintContentsCode: string
  expirationDate: string
  previewHtml: string
  downloadActiveCollapses: string[]
  otherSizesCollapse: string[]
  printFlag: string
  formatid: string
  paperInfo: string
  paperNumb: string
  paperNumb2: string
  printType: string
  selFontStyle4DB: string
  resumeAddress: string
  showMap: boolean
  mapError: string
  isGeolocating: boolean
  isGeocodingAddress: boolean
  selectedStore: string | null
  isQRCodeLoaded: boolean
  qrCodeError: boolean
  qrCodeRetryCount: number
  qrCodeCacheBuster: number
  isSevenElevenButtonDisabled: boolean
  paypayEprintUrl: string
  merchantPaymentId: string
  isPaypayProcessing: boolean
  paypayCodeExpiryFallbackMs: number
  paypayExpiryGraceMs: number
  paypayRecheckDelayMs: number
  paypayRecheckCount: number
  paypayRecheckMaxCount: number
  isEprintProcessing: boolean
  isCreditCardProcessing: boolean
  eprintPDFSending: boolean
  eprintProcessingPending: boolean
  isPrePayment: boolean
  isRefundProcessing: boolean
  lastPaymentData: EprintPaymentData | null
  eprintPrice: number
  funnelSessionId: string | null
  dialogOpenedAt: Date | null
  previousClickedPrintType: string | null
  viewedSections: Record<string, boolean>
  isMypage: boolean
  paypayNoticeVisible: boolean
  paymentSelectorApi: PaymentSelectorApi | null
  dialogLayoutReady: boolean
  paypayPendingMessage: { close: () => void } | null
  dialogRootEl: HTMLElement | null
}

const templateConfigs = templateConfigsJson as Record<
  string,
  EprintTemplateConfig
>

const EPRINT_SESSION_KEY: InjectionKey<EprintSession> = Symbol('eprintSession')

function createEmptyEprintSession(): EprintSession {
  return {
    visible: false,
    eprintPdfEnabled: true,
    resultname: '',
    isNotLetter: true,
    eprintResultCode: '100',
    eprintContentsCode: '',
    expirationDate: '',
    previewHtml: '',
    downloadActiveCollapses: [''],
    otherSizesCollapse: [],
    printFlag: 'basic',
    formatid: '',
    paperInfo: '',
    paperNumb: '1',
    paperNumb2: '3',
    printType: '7',
    selFontStyle4DB: '',
    resumeAddress: '',
    showMap: false,
    mapError: '',
    isGeolocating: false,
    isGeocodingAddress: false,
    selectedStore: null,
    isQRCodeLoaded: true,
    qrCodeError: false,
    qrCodeRetryCount: 0,
    qrCodeCacheBuster: 0,
    isSevenElevenButtonDisabled: false,
    paypayEprintUrl: '',
    merchantPaymentId: '',
    isPaypayProcessing: false,
    paypayCodeExpiryFallbackMs: 300 * 1000,
    paypayExpiryGraceMs: 30 * 1000,
    paypayRecheckDelayMs: 2000,
    paypayRecheckCount: 0,
    paypayRecheckMaxCount: 15,
    isEprintProcessing: false,
    isCreditCardProcessing: false,
    eprintPDFSending: false,
    eprintProcessingPending: false,
    isPrePayment: false,
    isRefundProcessing: false,
    lastPaymentData: null,
    eprintPrice: 450,
    funnelSessionId: null,
    dialogOpenedAt: null,
    previousClickedPrintType: null,
    viewedSections: {},
    isMypage: false,
    paypayNoticeVisible: false,
    paymentSelectorApi: null,
    dialogLayoutReady: false,
    paypayPendingMessage: null,
    dialogRootEl: null
  }
}

export function provideEprintSession() {
  const session = reactive(createEmptyEprintSession())
  provide(EPRINT_SESSION_KEY, session)
  return session
}

export function provideEprintActions(actions: EprintActions) {
  provide(EPRINT_ACTIONS_KEY, actions)
  return actions
}

export function useEprintSession(): EprintSession {
  const session = inject(EPRINT_SESSION_KEY)
  if (!session) {
    throw new Error('useEprintSession() must be used under EprintDialog')
  }
  return session
}

export function useEprintActions(): EprintActions {
  const actions = inject(EPRINT_ACTIONS_KEY)
  if (!actions) {
    throw new Error('useEprintActions() must be used under EprintDialog')
  }
  return actions
}

export function useEprintDerived(session: EprintSession) {
  const currentPaymentPrice = computed(() => session.eprintPrice)
  const currentTemplateConfig = computed(
    () => templateConfigs[session.printFlag] || templateConfigs.basic
  )
  const recommendedOption = computed(() => {
    const config = currentTemplateConfig.value
    return config.options.find((option) => option.id === config.recommended)
  })
  const otherOptions = computed(() => {
    const config = currentTemplateConfig.value
    return config.options.filter((option) => option.id !== config.recommended)
  })
  const hasRecommendedOption = computed(() => !!recommendedOption.value)
  const hasOtherOptions = computed(() => otherOptions.value.length > 0)
  const isLoading = computed(
    () =>
      session.isPaypayProcessing ||
      session.isEprintProcessing ||
      session.isCreditCardProcessing ||
      session.eprintPDFSending ||
      session.isRefundProcessing
  )

  function defaultEprintPaymentMethod(appFlag: string): string {
    if (!isCashlessSupported({ id: session.printType })) {
      return 'convinience'
    }
    return appFlag === 'b' ? 'card' : 'paypay'
  }

  return {
    templateConfigs,
    currentPaymentPrice,
    currentTemplateConfig,
    recommendedOption,
    otherOptions,
    hasRecommendedOption,
    hasOtherOptions,
    isLoading,
    defaultEprintPaymentMethod
  }
}
